import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ServiceCarousel from '../../components/feature/ServiceCarousel';
import ServicesCarousel from '../../components/feature/ServicesCarousel';
import Button from '../../components/base/Button';

export default function HomePage() {
  const stats = [
    { number: '500+', label: 'Real Estate Agents', icon: 'ri-user-3-fill' },
    { number: '10,000+', label: 'Leads Generated', icon: 'ri-customer-service-2-fill' },
    { number: '95%', label: 'Client Satisfaction', icon: 'ri-star-fill' },
    { number: '24/7', label: 'AI Support', icon: 'ri-time-fill' }
  ];

  const features = [
    {
      icon: 'ri-brain-fill',
      title: 'AI Lead Generation',
      description: 'Automatically identify and qualify high-quality leads using advanced AI algorithms and market data analysis.'
    },
    {
      icon: 'ri-list-check-3',
      title: 'Listing Automation',
      description: 'Create compelling property listings with AI-generated descriptions, optimal pricing, and market positioning.'
    },
    {
      icon: 'ri-line-chart-fill',
      title: 'Market Insights',
      description: 'Get real-time market analytics, price predictions, and investment opportunities powered by AI.'
    },
    {
      icon: 'ri-customer-service-fill',
      title: 'Client Management',
      description: 'Streamline client relationships with AI-powered CRM, automated follow-ups, and personalized communication.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Top Real Estate Agent',
      company: 'Century 21',
      image: 'https://public.readdy.ai/ai/img_res/ea8a0e2f52756eabd197085ea3ce8edf.jpg',
      quote: 'AI Realty Pro transformed my business. I\'ve increased my lead generation by 300% and closed more deals than ever before.'
    },
    {
      name: 'Michael Chen',
      role: 'Broker Owner',
      company: 'RE/MAX Elite',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20real%20estate%20broker%20headshot%2C%20confident%20expression%2C%20suit%20and%20tie%2C%20modern%20office%20setting%2C%20executive%20portrait%20style&width=80&height=80&seq=7&orientation=squarish',
      quote: 'The AI staging feature alone has helped us sell properties 40% faster. This platform is a game-changer for our industry.'
    },
    {
      name: 'Jennifer Martinez',
      role: 'Luxury Agent',
      company: 'Sotheby\'s International',
      image: 'https://readdy.ai/api/search-image?query=Professional%20female%20luxury%20real%20estate%20agent%20headshot%2C%20elegant%20appearance%2C%20designer%20clothing%2C%20upscale%20office%20background%2C%20sophisticated%20portrait&width=80&height=80&seq=8&orientation=squarish',
      quote: 'The AI clone service works around the clock for me. My clients love the instant responses and personalized service.'
    }
  ];

  const companyValues = [
    {
      icon: 'ri-lightbulb-fill',
      title: 'Innovation First',
      description: 'We continuously push the boundaries of AI technology to deliver cutting-edge solutions for real estate professionals.'
    },
    {
      icon: 'ri-shield-check-fill',
      title: 'Trust & Security',
      description: 'Your data and client information are protected with enterprise-grade security and privacy measures.'
    },
    {
      icon: 'ri-team-fill',
      title: 'Client Success',
      description: 'Our dedicated support team ensures you maximize the potential of our AI platform for your business growth.'
    },
    {
      icon: 'ri-rocket-fill',
      title: 'Proven Results',
      description: 'Track record of helping real estate professionals increase leads by 300% and close deals 40% faster.'
    }
  ];

  const teamMembers = [
    {
      name: 'David Rodriguez',
      role: 'CEO & Founder',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20CEO%20headshot%2C%20confident%20leadership%20expression%2C%20modern%20business%20suit%2C%20executive%20office%20background%2C%20corporate%20portrait%20style%20with%20clean%20lighting&width=300&height=300&seq=10&orientation=squarish',
      bio: 'Former real estate broker turned tech entrepreneur with 15 years of industry experience.'
    },
    {
      name: 'Emily Watson',
      role: 'CTO & AI Lead',
      image: 'https://readdy.ai/api/search-image?query=Professional%20female%20technology%20executive%20headshot%2C%20intelligent%20expression%2C%20modern%20business%20attire%2C%20tech%20office%20background%2C%20corporate%20portrait%20with%20professional%20lighting&width=300&height=300&seq=11&orientation=squarish',
      bio: 'AI researcher and former Google engineer specializing in machine learning and natural language processing.'
    },
    {
      name: 'Marcus Thompson',
      role: 'VP of Sales',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20sales%20executive%20headshot%2C%20friendly%20confident%20smile%2C%20business%20suit%2C%20modern%20office%20setting%2C%20corporate%20portrait%20style%20with%20warm%20lighting&width=300&height=300&seq=12&orientation=squarish',
      bio: 'Real estate industry veteran with expertise in sales automation and lead generation strategies.'
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.8), rgba(59, 130, 246, 0.6)), url('https://readdy.ai/api/search-image?query=Modern%20real%20estate%20office%20with%20AI%20technology%20displays%2C%20futuristic%20workspace%20with%20holographic%20property%20listings%2C%20professional%20agents%20using%20advanced%20technology%2C%20sleek%20blue%20and%20white%20interior%20design%2C%20large%20windows%20with%20city%20skyline%20view&width=1920&height=1080&seq=9&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight font-sans">
              Transform Your Real Estate Business with <span className="text-blue-300">AI Power</span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed font-sans">
              Automate lead generation, streamline listings, and close more deals with our comprehensive AI platform designed specifically for real estate professionals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/demo">
                <Button size="lg" className="text-lg px-8 py-4 font-sans">
                  <i className="ri-play-circle-fill mr-2"></i>
                  Start Free Demo
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" size="lg" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-blue-600 font-sans">
                  <i className="ri-arrow-right-line mr-2"></i>
                  Explore Services
                </Button>
              </Link>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                    <i className={`${stat.icon} text-white text-xl`}></i>
                  </div>
                  <div className="text-3xl font-bold text-white font-sans">{stat.number}</div>
                  <div className="text-blue-200 text-sm font-sans">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Carousel */}
      <ServiceCarousel />

      {/* About Company Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 font-sans">
                Leading the Future of Real Estate Technology
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed font-sans">
                Founded in 2020 by real estate professionals who understood the industry's need for intelligent automation, AI Realty Pro has become the trusted partner for over 500 agents nationwide.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed font-sans">
                Our mission is to empower real estate professionals with cutting-edge AI technology that eliminates repetitive tasks, generates qualified leads, and accelerates business growth. We believe that by combining human expertise with artificial intelligence, we can transform how real estate is bought and sold.
              </p>
              <div className="flex space-x-4">
                <Link to="/about">
                  <Button size="lg" className="font-sans">
                    <i className="ri-information-fill mr-2"></i>
                    Learn More About Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20real%20estate%20technology%20company%20office%20interior%2C%20diverse%20team%20of%20professionals%20working%20with%20AI%20technology%2C%20collaborative%20workspace%20with%20large%20screens%20showing%20property%20data%2C%20bright%20natural%20lighting%2C%20professional%20business%20environment&width=600&height=500&seq=13&orientation=landscape"
                alt="AI Realty Pro Office"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 font-sans">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              The principles that drive everything we do and guide our commitment to revolutionizing the real estate industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyValues.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 font-sans">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed font-sans">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 font-sans">
              Meet Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              Industry experts and technology leaders dedicated to transforming real estate through artificial intelligence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="text-center group">
                <div className="relative mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-48 h-48 rounded-full object-cover mx-auto shadow-lg group-hover:shadow-xl transition-shadow"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-full"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2 font-sans">{member.name}</h3>
                <p className="text-blue-600 font-semibold mb-4 font-sans">{member.role}</p>
                <p className="text-gray-600 leading-relaxed font-sans">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 font-sans">
              Why Choose AI Realty Pro?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              Our AI-powered platform provides everything you need to dominate your local real estate market and scale your business efficiently.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center group hover:transform hover:scale-105 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:shadow-xl transition-shadow">
                  <i className={`${feature.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 font-sans">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed font-sans">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 font-sans">
              Trusted by Top Real Estate Professionals
            </h2>
            <p className="text-xl text-gray-600 font-sans">
              See how our AI platform is transforming real estate businesses across the country.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900 font-sans">{testimonial.name}</h4>
                    <p className="text-blue-600 font-medium font-sans">{testimonial.role}</p>
                    <p className="text-gray-500 text-sm font-sans">{testimonial.company}</p>
                  </div>
                </div>
                <p className="text-gray-700 italic leading-relaxed font-sans">"{testimonial.quote}"</p>
                <div className="flex text-yellow-400 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6 font-sans">
            Ready to Revolutionize Your Real Estate Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto font-sans">
            Join hundreds of successful agents who are already using AI to generate more leads, close more deals, and grow their business exponentially.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4 font-sans">
                <i className="ri-rocket-fill mr-2"></i>
                Get Started Free
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8 py-4 font-sans">
                <i className="ri-phone-fill mr-2"></i>
                Schedule Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* New Services Carousel */}
      <ServicesCarousel />

      {/* Duplicate AI Chatbots Section - Added before Footer */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-white p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
                      <i className="ri-chat-3-fill text-white text-2xl"></i>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">AI Chatbots</h3>
                  </div>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Intelligent chatbots that handle inquiries, schedule viewings, and nurture leads while you focus on closing deals.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-500 text-lg"></i>
                      <span className="text-gray-700 font-medium">Instant Response</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-500 text-lg"></i>
                      <span className="text-gray-700 font-medium">Lead Qualification</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-500 text-lg"></i>
                      <span className="text-gray-700 font-medium">Appointment Scheduling</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-500 text-lg"></i>
                      <span className="text-gray-700 font-medium">CRM Integration</span>
                    </div>
                  </div>
                  <div className="flex space-x-4">
                    <Link to="/ai-tools/chatbots">
                      <Button variant="primary" size="lg" className="rounded-xl">
                        Learn More
                      </Button>
                    </Link>
                    <Link to="/demo">
                      <Button variant="outline" size="lg" className="rounded-xl">
                        Try Demo
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20chatbot%20interface%20on%20smartphone%20screen%20showing%20real%20estate%20conversation%2C%20clean%20UI%20design%2C%20property%20listings%20visible%2C%20professional%20blue%20theme%2C%20minimalist%20background&width=600&height=400&seq=chatbot-duplicate&orientation=landscape"
                    alt="AI Chatbots"
                    className="w-full h-80 object-cover rounded-xl shadow-lg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Call-to-Action Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-bold text-white mb-6 font-sans">
              Don't Let Your Competition Get Ahead
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed font-sans">
              While you're reading this, other agents are already using AI to capture leads, automate listings, and close more deals. Join the AI revolution today and dominate your market tomorrow.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-fill text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-sans">Setup in 5 Minutes</h3>
                <p className="text-blue-100 font-sans">Get started instantly with our quick setup wizard</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-fill text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-sans">30-Day Guarantee</h3>
                <p className="text-blue-100 font-sans">Risk-free trial with full money-back guarantee</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-fill text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-sans">24/7 Support</h3>
                <p className="text-blue-100 font-sans">Expert support team ready to help you succeed</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/demo">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-4 font-sans shadow-xl">
                  <i className="ri-rocket-fill mr-2"></i>
                  Start Your Free Trial Now
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 text-lg px-10 py-4 font-sans">
                  <i className="ri-phone-fill mr-2"></i>
                  Talk to an Expert
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center space-x-6 text-blue-200">
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-green-400"></i>
                <span className="font-sans">No Credit Card Required</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-green-400"></i>
                <span className="font-sans">Cancel Anytime</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-green-400"></i>
                <span className="font-sans">Instant Access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
